// api
import type {
  GetEnergyGroupDto,
  PutEnergyGroupDto,
  PutEnergyTreeNodeOptionsDto,
  SpecialNodeType,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';
import type {
  BaseLocationDto,
  LocationDto,
} from '@/api/generated/recalc/endpoints/v2/locations';

// shared components
import { EMPTY_ADDRESS_VALUE } from '@/components/ui/form';
import type { DanishAddressFieldValue } from '@/components/ui/form';

// feature-utils
import { DEFAULT_OPENING_HOURS } from '@/features/energyxplorer/utils/openingHoursDefaults';

// relative
import type {
  EditEnergyGroupFormValues,
  OpeningHoursValue,
} from './editEnergyGroupForm.schema';

/** The subset of a root energy tree node's options this form reads/writes. */
export interface NormalizedEnergyTreeNodeOptions {
  specialNodeType: SpecialNodeType | null;
  percentage: number;
  consumption: number;
  openingHours: OpeningHoursValue;
  goShares: unknown[];
}

/** An energy group known to be fully loaded (as opposed to `GetEnergyGroupDto`'s all-optional fields). */
export type LoadedEnergyGroup = GetEnergyGroupDto & {
  energyGroupId: number;
  locationId: number;
  type: NonNullable<GetEnergyGroupDto['type']>;
};

function toLocationFieldValue(location?: LocationDto): DanishAddressFieldValue {
  if (!location) return EMPTY_ADDRESS_VALUE;

  return {
    displayName: location.displayName ?? '',
    street: location.street ?? null,
    houseNumber: location.houseNumber ?? null,
    floor: location.floor ?? null,
    door: location.door ?? null,
    zipCode: location.zipCode ?? null,
    city: location.city ?? null,
    country: location.country ?? null,
    latitude: location.latitude ?? null,
    longitude: location.longitude ?? null,
    coordinateAccuracyMeters: location.coordinateAccuracyMeters ?? null,
    coordinateSource: location.coordinateSource ?? null,
    timezone: location.timezone ?? null,
    energyPriceZone: location.energyPriceZone ?? null,
    municipalityCode: location.municipalityCode ?? null,
    cadastreKey: location.cadastreKey ?? null,
  };
}

export function toEditEnergyGroupFormValues(
  energyGroup?: GetEnergyGroupDto,
  location?: LocationDto,
  openingHours?: OpeningHoursValue,
): EditEnergyGroupFormValues {
  return {
    name: energyGroup?.name ?? '',
    location: toLocationFieldValue(location),
    openingHours: openingHours ?? (DEFAULT_OPENING_HOURS as OpeningHoursValue),
  };
}

export function toPutEnergyGroupDto(
  values: EditEnergyGroupFormValues,
  energyGroup: LoadedEnergyGroup,
): PutEnergyGroupDto {
  return {
    name: values.name.trim(),
    type: energyGroup.type,
    buildingId: energyGroup.buildingId ?? null,
    locationId: energyGroup.locationId,
  };
}

export function toPutLocationDto(
  values: EditEnergyGroupFormValues,
): BaseLocationDto {
  // Safe cast: zod's `.refine()` on `location` guarantees `latitude`/
  // `longitude` are finite numbers by the time this mapper runs (only ever
  // called from the submit handler, after validation has passed).
  return {
    ...values.location,
    latitude: values.location.latitude as number,
    longitude: values.location.longitude as number,
  };
}

export function toPutEnergyTreeNodeOptionsDto(
  values: EditEnergyGroupFormValues,
  normalizedNodeOptions: NormalizedEnergyTreeNodeOptions,
): PutEnergyTreeNodeOptionsDto {
  return {
    specialNodeType: normalizedNodeOptions.specialNodeType ?? null,
    percentage: normalizedNodeOptions.percentage ?? 0,
    consumption: normalizedNodeOptions.consumption ?? 0,
    openingHours: values.openingHours,
    goShares: Array.isArray(normalizedNodeOptions.goShares)
      ? normalizedNodeOptions.goShares
      : [],
  };
}
